import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { Activity, City, Country, Dictionary, ProfessionalPosition, OtherProfessionalPosition, StreetType, Trader, User, UserClient, RoleName } from '../../domain/data';
import {
    ActivityInterface,
    AdvisoryModel,
    CityInterface,
    CountryInterface,
    DictionaryInterface,
    IdInterface,
    ProfessionalPositionInterface,
    OtherProfessionalPositionInterface,
    StreetTypeInterface,
    TraderInterface,
    UserInterface,
    RoleNameInterface,
} from '../../../core';
import { AdvisoryModelAdapter } from '../helpers/serializer/adapters/advisory-model.adapter';
import { AppConfig } from '../../utils/app-config.service';
import { LocalStorage } from '../helpers/local-storage';
import { MyHttpClient } from './my-http-client';
import { Serializer } from '../helpers/serializer/serializer';

import { environment } from '../../../../environments/environment';

@Injectable()
export class UserApiClient {
    constructor(private http: MyHttpClient, private localStorage: LocalStorage, private serializer: Serializer) {}

    getActivities(): Observable<ActivityInterface[]> {
        return of(`${environment.baseHref}assets/mock/activities.json?v=${environment.version}`).pipe(
            switchMap((url) => {
                return this.http.get(url).pipe(map((resp: any) => this.serializer.fromJsonArray(resp, Activity)));
            })
        );
    }

    getAdvisoryModels(): Observable<AdvisoryModel[]> {
        return of(AppConfig.settings.services.base + environment.services.paths.get_advisory_models).pipe(
            switchMap((url) => {
                return this.http.post(url, null).pipe(
                    map((resp: any) => {
                        if (!resp || (resp.Status !== 'OK' && resp.Status !== 'ok' && resp.Status !== '')) {
                            throw new Error(resp ? resp.Status : 'error.general');
                        } else {
                            const adapter = new AdvisoryModelAdapter();

                            return (resp.Data as any[])
                                .map((value) => adapter.deserialize(value.ServiceTypeCode))
                                .filter((value) => value !== undefined);
                        }
                    })
                );
            })
        );
    }

    getCities(countryId: IdInterface): Observable<CityInterface[]> {
        return of(`${environment.baseHref}assets/mock/cities.json?v=${environment.version}`).pipe(
            switchMap((url) => {
                if (String(countryId) === 'ES') {
                    return this.http.get(url).pipe(map((resp: any) => this.serializer.fromJsonArray(resp, City)));
                } else {
                    return of([]);
                }
            })
        );
    }

    getCountries(): Observable<CountryInterface[]> {
        return of(`${environment.baseHref}assets/mock/countries.json?v=${environment.version}`).pipe(
            switchMap((url) => {
                return this.http.get(url).pipe(map((resp: any) => this.serializer.fromJsonArray(resp, Country)));
            })
        );
    }

    getDictionary(): Observable<DictionaryInterface[]> {
        return of(`${environment.baseHref}assets/mock/dictionary.json?v=${environment.version}`).pipe(
            switchMap((url) => {
                return this.http.get(url).pipe(map((resp: any) => this.serializer.fromJsonArray(resp, Dictionary)));
            })
        );
    }

    getExternalTraders(): Observable<TraderInterface[]> {
        return of(`${environment.baseHref}assets/mock/external-traders.json?v=${environment.version}`).pipe(
            switchMap((url) => {
                return this.http.get(url).pipe(map((resp: any) => this.serializer.fromJsonArray(resp, Trader)));
            })
        );
    }

    getNormativeCountries(): Observable<CountryInterface[]> {
        return of(`${environment.baseHref}assets/mock/normative-countries.json?v=${environment.version}`).pipe(
            switchMap((url) => {
                return this.http.get(url).pipe(map((resp: any) => this.serializer.fromJsonArray(resp, Country)));
            })
        );
    }

    getProfessionalPositions(): Observable<ProfessionalPositionInterface[]> {
        return of(`${environment.baseHref}assets/mock/professional-positions.json?v=${environment.version}`).pipe(
            switchMap((url) => {
                return this.http.get(url).pipe(map((resp: any) => this.serializer.fromJsonArray(resp, ProfessionalPosition)));
            })
        );
    }

    getOtherProfessionalPositions(): Observable<OtherProfessionalPositionInterface[]> {
        return of(`${environment.baseHref}assets/mock/other-professional-positions.json?v=${environment.version}`).pipe(
            switchMap((url) => {
                return this.http.get(url).pipe(map((resp: any) => this.serializer.fromJsonArray(resp, OtherProfessionalPosition)));
            })
        );
    }

    getRoleName(): Observable<RoleNameInterface[]> {
        return of(`${environment.baseHref}assets/mock/role-name.json?v=${environment.version}`).pipe(
            switchMap((url) => {
                return this.http.get(url).pipe(map((resp: any) => this.serializer.fromJsonArray(resp, RoleName)));
            })
        );
    }

    getProfile(): Observable<UserInterface> {
        return of(AppConfig.settings.services.base + environment.services.paths.get_profile).pipe(
            switchMap((url) => {
                return this.http.post(url, null).pipe(
                    map((resp: any) => {
                        if (environment.portalClient) {
                            return this.serializer.fromJson(resp, UserClient);
                        }

                        return this.serializer.fromJson(resp, User);
                    })
                );
            })
        );
    }

    getStreetTypes(): Observable<StreetTypeInterface[]> {
        return of(`${environment.baseHref}assets/mock/street-types.json?v=${environment.version}`).pipe(
            switchMap((url) => {
                return this.http.get(url).pipe(map((resp: any) => this.serializer.fromJsonArray(resp, StreetType)));
            })
        );
    }

    getTraders(): Observable<TraderInterface[]> {
        return of(`${environment.baseHref}assets/mock/traders.json?v=${environment.version}`).pipe(
            switchMap((url) => {
                return this.http.get(url).pipe(map((resp: any) => this.serializer.fromJsonArray(resp, Trader)));
            })
        );
    }
}
